import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { CallAnalysis, CallFilters, SortConfig } from "../types/api";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  UserPlusIcon,
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AddSalespersonModal from "../components/forms/AddSalespersonModal";

const TeamAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState<CallAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState<CallFilters>({
    page: 1,
    limit: 20,
    sortBy: "created_at",
    sortOrder: "desc",
  });

  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [minGrade, setMinGrade] = useState<number | "">("");
  const [maxGrade, setMaxGrade] = useState<number | "">("");
  const [objectiveFilter, setObjectiveFilter] = useState<string>("all");
  const [redFlagsFilter, setRedFlagsFilter] = useState<string>("all");

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "created_at",
    direction: "desc",
  });

  const loadCalls = useCallback(
    async (newFilters?: CallFilters) => {
      setLoading(true);
      try {
        const filtersToUse = newFilters || filters;
        const response = await apiService.getCalls(filtersToUse);
        if (response.success) {
          setCalls(response.data.analyses || []);
          setTotalCount(response.data.totalCount || 0);
          setCurrentPage(response.data.currentPage || 1);
          setTotalPages(response.data.totalPages || 1);
        } else {
          setCalls([]);
          setTotalCount(0);
          setCurrentPage(1);
          setTotalPages(1);
        }
      } catch {
        setCalls([]);
        setTotalCount(0);
        setCurrentPage(1);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  const handleSort = (key: string) => {
    const direction: "asc" | "desc" =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    const newFilters = { ...filters, sortBy: key, sortOrder: direction };
    setFilters(newFilters);
    loadCalls(newFilters);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadCalls(newFilters);
  };

  const handleLimitChange = (limit: number) => {
    const newFilters = { ...filters, limit, page: 1 };
    setFilters(newFilters);
    loadCalls(newFilters);
  };

  const applyFilters = () => {
    const newFilters = {
      ...filters,
      page: 1,
      dateFrom: dateFrom?.toISOString().split("T")[0],
      dateTo: dateTo?.toISOString().split("T")[0],
      minGrade: minGrade === "" ? undefined : Number(minGrade),
      maxGrade: maxGrade === "" ? undefined : Number(maxGrade),
      objectiveAchieved:
        objectiveFilter === "all" ? undefined : objectiveFilter,
      redFlags: redFlagsFilter === "all" ? undefined : redFlagsFilter,
    };
    setFilters(newFilters);
    loadCalls(newFilters);
  };

  const clearFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setMinGrade("");
    setMaxGrade("");
    setObjectiveFilter("all");
    setRedFlagsFilter("all");
    const newFilters = {
      ...filters,
      page: 1,
      dateFrom: undefined,
      dateTo: undefined,
      minGrade: undefined,
      maxGrade: undefined,
      objectiveAchieved: undefined,
      redFlags: undefined,
    };
    setFilters(newFilters);
    loadCalls(newFilters);
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "completed":
        return { color: "#16a34a" };
      case "processing":
      case "transcribing":
      case "analyzing":
        return { color: "#d97706" };
      case "failed":
        return { color: "#dc2626" };
      default:
        return { color: "#9ca3af" };
    }
  };

  const getGradeStyle = (grade?: number) => {
    if (!grade) return { color: "#9ca3af", label: "—" };
    if (grade >= 8) return { color: "#16a34a", label: grade.toString() };
    if (grade >= 6) return { color: "#d97706", label: grade.toString() };
    return { color: "#dc2626", label: grade.toString() };
  };

  const isInsufficientData = (call: CallAnalysis) =>
    call.analysis?.insufficient_data === true ||
    (call.average_grade === null && call.status === "completed");

  const getOutcome = (objectiveAchieved: boolean | null | undefined) => {
    if (objectiveAchieved == null)
      return { bg: "#f3f4f6", text: "Unknown", textColor: "#9ca3af" };
    return objectiveAchieved
      ? { bg: "#dcfce7", text: "Achieved", textColor: "#166534" }
      : { bg: "#fee2e2", text: "Not Achieved", textColor: "#dc2626" };
  };

  const SortIcon: React.FC<{ column: string }> = ({ column }) => {
    const active = sortConfig.key === column;
    return sortConfig.direction === "asc" && active ? (
      <ChevronUpIcon
        style={{ width: 12, height: 12, color: active ? "#111" : "#d1d5db" }}
      />
    ) : (
      <ChevronDownIcon
        style={{ width: 12, height: 12, color: active ? "#111" : "#d1d5db" }}
      />
    );
  };

  const startRecord =
    totalCount === 0
      ? 0
      : Math.min((currentPage - 1) * (filters.limit || 20) + 1, totalCount);
  const endRecord =
    totalCount === 0
      ? 0
      : Math.min(currentPage * (filters.limit || 20), totalCount);

  const hasActiveFilters =
    filters.dateFrom ||
    filters.dateTo ||
    filters.minGrade !== undefined ||
    filters.maxGrade !== undefined ||
    filters.objectiveAchieved !== undefined ||
    filters.redFlags !== undefined;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600&family=Geist+Mono:wght@400;500&display=swap');

        .ta-root {
          font-family: 'Geist', -apple-system, sans-serif;
          color: #111;
          --border: #e5e7eb;
          --muted: #6b7280;
          --accent: #2563eb;
          --radius: 8px;
        }

        .ta-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          background: #111;
          color: #fff;
          border: none;
          border-radius: var(--radius);
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          letter-spacing: -0.01em;
          transition: background 0.15s;
        }
        .ta-btn-primary:hover { background: #333; }

        .ta-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          background: #fff;
          color: #374151;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          letter-spacing: -0.01em;
          transition: all 0.15s;
        }
        .ta-btn-secondary:hover { background: #f9fafb; border-color: #d1d5db; }
        .ta-btn-secondary.active { background: #f0f0f0; border-color: #111; color: #111; }

        .ta-btn-ghost {
          display: inline-flex;
          align-items: center;
          padding: 6px 10px;
          background: none;
          color: var(--muted);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          font-size: 0.78rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          transition: all 0.15s;
        }
        .ta-btn-ghost:hover:not(:disabled) { background: #f9fafb; color: #111; }
        .ta-btn-ghost:disabled { opacity: 0.35; cursor: not-allowed; }

        .filter-panel {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 20px;
          animation: slideDown 0.15s ease;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .filter-label {
          display: block;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 6px;
        }

        .filter-input {
          width: 100%;
          padding: 7px 10px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.82rem;
          font-family: 'Geist', sans-serif;
          color: #111;
          background: #fff;
          outline: none;
          transition: border-color 0.15s;
          box-sizing: border-box;
        }
        .filter-input:focus { border-color: #111; }

        .filter-select {
          width: 100%;
          padding: 7px 10px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.82rem;
          font-family: 'Geist', sans-serif;
          color: #111;
          background: #fff;
          outline: none;
          cursor: pointer;
          transition: border-color 0.15s;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 10px center;
          padding-right: 30px;
        }
        .filter-select:focus { border-color: #111; }

        .pagination-bar {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .count-text {
          font-size: 0.8rem;
          color: var(--muted);
        }
        .count-text strong { color: #111; font-weight: 600; }

        .rows-select {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: var(--muted);
        }
        .rows-select select {
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 3px 8px;
          font-size: 0.78rem;
          font-family: 'Geist', sans-serif;
          color: #111;
          outline: none;
          background: #fff;
          cursor: pointer;
        }

        .page-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .page-display {
          font-size: 0.78rem;
          color: #374151;
          padding: 0 12px;
          font-variant-numeric: tabular-nums;
          font-family: 'Geist Mono', monospace;
        }

        .table-wrap {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 10px;
          overflow: hidden;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.83rem;
          table-layout: fixed; /* ADD THIS */
        }

        td, th {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        thead {
          border-bottom: 1px solid var(--border);
        }

        th {
          padding: 10px 16px;
          text-align: left;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: #9ca3af;
          white-space: nowrap;
          cursor: pointer;
          user-select: none;
          background: #fafafa;
        }
        th:hover { background: #f3f4f6; color: #374151; }

        .th-inner {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        td {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          color: #374151;
          vertical-align: middle;
        }

        tbody tr:last-child td { border-bottom: none; }

        tbody tr {
          cursor: pointer;
          transition: background 0.1s;
        }
        tbody tr:hover td { background: #fafafa; }
        tbody tr:hover td:first-child { background: #fafafa; }

        .cell-name {
          font-weight: 500;
          color: #111;
          font-size: 0.83rem;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 8px;
          border-radius: 100px;
          font-size: 0.72rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .grade-value {
          font-family: 'Geist Mono', monospace;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .outcome-cell {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 0.8rem;
        }
        .outcome-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .redflag-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 0.75rem;
          font-weight: 500;
          padding: 2px 8px;
          border-radius: 100px;
        }

        .date-cell {
          font-family: 'Geist Mono', monospace;
          font-size: 0.77rem;
          color: #9ca3af;
        }

        .file-cell {
          font-family: 'Geist Mono', monospace;
          font-size: 0.72rem;
          color: #9ca3af;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .loading-row td {
          padding: 48px;
          text-align: center;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #e5e7eb;
          border-top-color: #111;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        .empty-state {
          text-align: center;
          padding: 64px 24px;
          color: var(--muted);
          font-size: 0.85rem;
        }

        .active-filters-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .filter-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          background: #f0f0f0;
          border-radius: 100px;
          font-size: 0.72rem;
          font-weight: 500;
          color: #374151;
        }
      `}</style>

      <div
        className="ta-root"
        style={{ display: "flex", flexDirection: "column", gap: "16px" }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "1.4rem",
                fontWeight: 600,
                letterSpacing: "-0.03em",
                color: "#111",
                margin: 0,
              }}
            >
              All Calls
            </h1>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: "0.83rem",
                color: "#9ca3af",
              }}
            >
              Sales call performance across your team
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`ta-btn-secondary ${showFilters ? "active" : ""}`}
            >
              <FunnelIcon style={{ width: 13, height: 13 }} />
              Filters
              {hasActiveFilters && (
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#2563eb",
                    display: "inline-block",
                    marginLeft: 2,
                  }}
                />
              )}
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="ta-btn-primary"
            >
              <UserPlusIcon style={{ width: 13, height: 13 }} />
              Add Employee
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="filter-panel">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: "16px",
              }}
            >
              <div>
                <label className="filter-label">Date From</label>
                <DatePicker
                  selected={dateFrom}
                  onChange={(date: Date | null) => setDateFrom(date)}
                  className="filter-input"
                  placeholderText="Start date"
                  dateFormat="MMM d, yyyy"
                />
              </div>
              <div>
                <label className="filter-label">Date To</label>
                <DatePicker
                  selected={dateTo}
                  onChange={(date: Date | null) => setDateTo(date)}
                  className="filter-input"
                  placeholderText="End date"
                  dateFormat="MMM d, yyyy"
                  minDate={dateFrom || undefined}
                />
              </div>
              <div>
                <label className="filter-label">Min Grade</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.1"
                  value={minGrade}
                  placeholder="1.0"
                  onChange={(e) =>
                    setMinGrade(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="filter-input"
                />
              </div>
              <div>
                <label className="filter-label">Max Grade</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.1"
                  value={maxGrade}
                  placeholder="10.0"
                  onChange={(e) =>
                    setMaxGrade(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="filter-input"
                />
              </div>
              <div>
                <label className="filter-label">Outcome</label>
                <select
                  value={objectiveFilter}
                  onChange={(e) => setObjectiveFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All</option>
                  <option value="true">Achieved</option>
                  <option value="false">Not Achieved</option>
                  <option value="null">Unknown</option>
                </select>
              </div>
              <div>
                <label className="filter-label">Red Flags</label>
                <select
                  value={redFlagsFilter}
                  onChange={(e) => setRedFlagsFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All</option>
                  <option value="true">Has Red Flags</option>
                  <option value="false">No Red Flags</option>
                  <option value="null">Unknown</option>
                </select>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid #f3f4f6",
              }}
            >
              <button
                onClick={clearFilters}
                className="ta-btn-ghost"
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <XMarkIcon style={{ width: 12, height: 12 }} />
                Clear all
              </button>
              <button onClick={applyFilters} className="ta-btn-primary">
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Pagination Bar */}
        <div className="pagination-bar">
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <span className="count-text">
              <strong>
                {startRecord}–{endRecord}
              </strong>{" "}
              of <strong>{totalCount}</strong> calls
            </span>
            <div className="rows-select">
              <span>Show</span>
              <select
                value={filters.limit}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
          <div className="page-nav">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="ta-btn-ghost"
            >
              ←
            </button>
            <span className="page-display">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="ta-btn-ghost"
            >
              →
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrap">
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  {[
                    { key: "salesperson_name", label: "Name", width: "160px" },
                    { key: "average_grade", label: "Grade", width: "80px" },
                    {
                      key: "objective_achieved",
                      label: "Outcome",
                      width: "120px",
                    },
                    { key: "red_flags", label: "Red Flags", width: "100px" },
                    { key: "created_at", label: "Date", width: "120px" },
                    { key: "audio_file_name", label: "File", width: "180px" },
                    { key: "status", label: "Status", width: "110px" },
                  ].map(({ key, label, width }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      style={{ width }}
                    >
                      <div className="th-inner">
                        {label}
                        <SortIcon column={key} />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody
                style={{
                  opacity: loading ? 0.4 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                {calls.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">No calls found</div>
                    </td>
                  </tr>
                ) : (
                  calls.map((call) => {
                    const grade = isInsufficientData(call)
                      ? { color: "#d1d5db", label: "N/A" }
                      : getGradeStyle(call.average_grade ?? undefined);
                    const outcome = getOutcome(call.objective_achieved);

                    return (
                      <tr
                        key={call.id}
                        onClick={() => navigate(`/call/${call.id}`)}
                      >
                        <td>
                          <span className="cell-name">
                            {call.salesperson_name || "Unknown"}
                          </span>
                        </td>
                        <td>
                          <span
                            className="grade-value"
                            style={{ color: grade.color }}
                          >
                            {grade.label}
                          </span>
                        </td>
                        <td>
                          <span
                            className="status-badge"
                            style={{
                              background: outcome.bg,
                              color: outcome.textColor,
                            }}
                          >
                            {outcome.text}
                          </span>
                        </td>
                        <td
                          style={{ textAlign: "center", paddingRight: "50px" }}
                        >
                          {call.red_flags === true ? (
                            <span
                              style={{
                                display: "inline-block",
                                width: 40,
                                height: 26,
                                borderRadius: 4,
                                background: "#ef4444",
                              }}
                            />
                          ) : null}
                        </td>
                        <td>
                          <span className="date-cell">
                            {new Date(call.created_at).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </span>
                        </td>
                        <td>
                          <span className="file-cell">
                            {call.audio_file_name}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              color: getStatusStyle(call.status).color,
                              textTransform: "capitalize",
                            }}
                          >
                            {call.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddSalespersonModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => setShowAddModal(false)}
      />
    </>
  );
};

export default TeamAnalysis;
